import { Location, NgClass, AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  inject,
  OnDestroy,
  OnInit,
  viewChild,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { DataObject } from '@miq/shared-models';
import { asWithStateMemberType } from '../utils/member-state';
import { L10nTranslationService, L10nTranslationModule } from 'angular-l10n';
import { Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  NextStepValidatableComponent,
  Step,
  StepComponent,
  StepValidatableComponent,
  WizardControl,
  WizardDef,
} from './model/wizard.model';
import { WizardDriverService, WizardState } from './wizard-driver.service';
import { WizardProjectorDirective } from './wizard-projector.directive';
import { WIZARD_CONTROLS, WIZARD_DEF_TOKEN } from './wizard.constants';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { GoBackLinkComponent } from '../components/go-back-link/go-back-link.component';
/*
 * L2-R6: the whole wizard subsystem (manager service, overlay component, driver, projector
 * directive, models, constants) moved from `src/app/shared/wizard/**`. Its only host dependencies
 * were `DataObject`, the `member-state` decorator helpers (moved alongside, see ../utils) and
 * GoBackLinkComponent (moved in this same pass) — everything else is Angular/CDK/l10n. Old host
 * paths are re-export shims, so the ~40 host importers are unchanged.
 */

const INPUT_NAME_DATA_IN = 'dataIn';
const INPUT_NAME_OPTIONS = 'options';
const NG_UNDECLARED_INPUT_ERROR_CODE = 'NG0303';
const STEP_VALIDATION_DEBOUNCE_MS = 10;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-wizard',
  templateUrl: './wizard.component.html',
  styleUrl: './wizard.component.scss',
  imports: [
    MatButton,
    MatIcon,
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatTabGroup,
    MatTab,
    NgClass,
    MatTabLabel,
    WizardProjectorDirective,
    MatDialogActions,
    GoBackLinkComponent,
    AsyncPipe,
    UpperCasePipe,
    L10nTranslationModule
],
  host: {
    '[class.mat-dialog-container]': 'matContainerClass',
  },
})
export class WizardComponent implements OnInit, AfterContentInit, OnDestroy {
  protected destroyRef = inject(DestroyRef);
  readonly wizardProjector = viewChild(WizardProjectorDirective);
  matContainerClass = true;
  wizardDriverService: WizardDriverService;
  stepTabs: Array<{ key: string; step: Step<StepComponent> }>;
  wizardTitle: string;
  wizardSubTitle: string;
  stepReady = false;
  firstStep: Step<StepComponent>;
  lastStep: Step<StepComponent>;
  isLast = false;
  isFirst = false;
  private currentStepName: string;
  private stepStateStore: { [key: string]: DataObject } = {};
  private componentRef: ComponentRef<StepComponent>;
  private dataOutEventSubscription: Subscription;
  private showStopButtonEventSubscription: Subscription;
  public lastButtonText: string;
  public hideBackButton: boolean;
  public showStopButton: boolean;
  public dataValue: any;
  private readonly wizardDef = inject<WizardDef>(WIZARD_DEF_TOKEN);
  private readonly control = inject<WizardControl>(WIZARD_CONTROLS);
  public translationService = inject(L10nTranslationService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly translateService = inject(L10nTranslationService);
  private readonly cdr = inject(ChangeDetectorRef);
  ngOnInit() {
    this.wizardTitle = this.wizardDef.title;
    this.wizardSubTitle = this.wizardDef.subTitle;
    this.lastButtonText =
      this.wizardDef.lastButtonText !== undefined ? this.wizardDef.lastButtonText : 'recordsSearch.done';
    this.hideBackButton = !!this.wizardDef.hideBackButton;
    /* required by translation service*/
    this.wizardDriverService = new WizardDriverService(this.wizardDef);
    this.wizardDriverService.start();
    this.stepTabs = this.wizardDriverService.stepsKeys;
    const [firstStep] = this.wizardDriverService.steps;
    const [lastStep] = [...this.wizardDriverService.steps].reverse();
    if (!firstStep || !lastStep) {
      return;
    }
    this.firstStep = firstStep;
    this.lastStep = lastStep;
    // close on navigation events
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        tap(() => this.onNavClose()),
        take(1)
      )
      .subscribe();
  }
  ngAfterContentInit() {
    this.wizardDriverService.currentStep$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(currentStep => {
          // dispose current event subscription (if any) on every step change
          this.disposeSubscriptions();
          // save step state variables
          this.saveStepState();
          this.updateFirstLastFlags(currentStep);
        }),
        withLatestFrom(this.wizardDriverService.data$)
      )
      .subscribe(([currentStep, data]) => {
        this.loadStep({ currentStep, data });
        this.cdr.markForCheck();
      });
    // close portal if finished
    this.wizardDriverService.finished$
      .pipe(
        take(1),
        tap(() => this.disposeSubscriptions()),
        withLatestFrom(this.wizardDriverService.data$),
        map(([, data]) => data)
      )
      .subscribe(data => this.control.close(data));
  }
  ngOnDestroy(): void {
    /* required by translation service*/
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
    this.wizardDriverService.destroy();
    if (this.wizardProjector()) {
      this.wizardProjector().ngOnDestroy();
    }
    this.stepStateStore = null;
  }
  /**
   * Updates first and last flag based on current step stepNumber
   * @param currentStep
   */
  private updateFirstLastFlags(currentStep: Step<StepComponent>): void {
    this.isFirst = currentStep.stepNumber === this.firstStep.stepNumber;
    this.isLast = currentStep.stepNumber === this.lastStep.stepNumber;
  }
  /**
   * Loads state.step component populating with current data accumulation
   * @param state {WizardState}
   */
  private loadStep(state: WizardState): void {
    if (!state?.currentStep?.component) {
      return;
    }
    this.currentStepName = state.currentStep.name;
    const { component } = state.currentStep;
    // const componentFactory = this.partyFactoryResolver.resolveComponentFactory(component.type);
    const viewContainerRef = this.wizardProjector().viewContainerRef;
    viewContainerRef.clear();
    const componentRef = viewContainerRef.createComponent(component.type);
    this.componentRef = componentRef;
    if (this.stepStateStore[this.currentStepName]) {
      Object.assign(componentRef.instance, this.stepStateStore[this.currentStepName]);
    }
    this.applyInput(componentRef, INPUT_NAME_DATA_IN, { ...component.args.dataIn, ...state.data });
    if (component.args?.options != null) {
      this.applyInput(componentRef, INPUT_NAME_OPTIONS, { ...component.args.options });
    }
    this.validateStep(state.currentStep, state.data);
    if (componentRef.instance.dataOut) {
      // StepSummary does not have emitters
      this.dataOutEventSubscription = outputToObservable(componentRef.instance.dataOut)
        .pipe(
          tap(changes => {
            this.wizardDriverService.updateData(changes);
            this.dataValue = changes;
            this.cdr.markForCheck();
          }),
          switchMap(() => this.wizardDriverService.data$)
        )
        .subscribe(data => this.validateStep(state.currentStep, data));
    }
    if (componentRef.instance.showStopButton) {
      this.showStopButtonEventSubscription = outputToObservable(componentRef.instance.showStopButton)
        .subscribe(flag => {
          this.showStopButton = flag;
          this.cdr.markForCheck();
        });
    }
  }

  private applyInput(componentRef: ComponentRef<StepComponent>, inputName: string, value: unknown): void {
    // setInput is the only safe way to set a signal input — direct property
    // assignment overwrites the InputSignal proxy with a plain value, making
    // `this.options()` throw "is not a function" inside the step's ngOnInit.
    // Inspecting ɵcmp.inputs to decide proactively is unreliable across Angular
    // versions (signal inputs are encoded differently), so we try setInput
    // first and fall back to direct assignment only if Angular rejects it
    // (NG0303: input not declared).
    try {
      componentRef.setInput(inputName, value);
    } catch (error: unknown) {
      const message = (error as { message?: string })?.message ?? '';
      // Only fall back for undeclared inputs (NG0303). Re-throw all other errors.
      if (!message.includes(NG_UNDECLARED_INPUT_ERROR_CODE)) {
        throw error;
      }
      (componentRef.instance as unknown as Record<string, unknown>)[inputName] = value;
    }
  }
  /**
   * Loads selected tab. Forward navigation is gated by stepReady;
   * backward navigation (clicking a previous step's tab) is always allowed.
   * @param stepIndex
   */
  loadStepByIndex(stepIndex: number): void {
    const tabStep = this.stepTabs?.[stepIndex]?.step;
    if (!tabStep) {
      return;
    }
    // Compare against the tab-sorted step list instead of the raw service steps array,
    // so tab index and looked-up step always refer to the same ordering.
    const currentStepNumber = tabStep.stepNumber;
    const activeStepNumber = this.stepTabs?.find(({ step }) => step.name === this.currentStepName)?.step.stepNumber
      ?? currentStepNumber;
    const isBackward = currentStepNumber < activeStepNumber;
    const canGoBack = !!this.wizardDef.allowBackwardTabNavigation && isBackward;
    if (this.stepReady || canGoBack) {
      this.showStopButton = false;
      this.wizardDriverService.loadStep(tabStep);
    }
  }
  /**
   * Verifies every targetData flagged as required to be populated
   * @param step currentStep
   * @param data data$ snapshot
   */
  private validateStep(step: Step<StepComponent>, data: DataObject): void {
    const targetData = step?.targetData ?? {};
    const dataValid = Object.keys(targetData)
      // validate only required
      .filter(field => !!targetData[field])
      .every(field => !!data[field]);
    const stepNameSnapshot = step.name;
    const componentValidSnapshot = this.getComponentValid();
    setTimeout(() => {
      // For opt-in flows (Space Lease wizard), ignore stale delayed validations
      // if the user has already navigated to a different step.
      if (this.wizardDef.allowBackwardTabNavigation && stepNameSnapshot !== this.currentStepName) {
        return;
      }
      const componentValid = this.wizardDef.allowBackwardTabNavigation ? this.getComponentValid() : componentValidSnapshot;
      this.stepReady = dataValid && componentValid;
      this.cdr.markForCheck();
    }, STEP_VALIDATION_DEBOUNCE_MS);
  }

  private getComponentValid(): boolean {
    if (!this.componentRef) {
      return true;
    }
    const validatableStepComponent = this.componentRef.instance as StepValidatableComponent;
    return typeof validatableStepComponent.valid === 'function' ? validatableStepComponent.valid() : true;
  }
  /**
   * unsubscribe current event listener if exists
   */
  private disposeSubscriptions() {
    if (this.dataOutEventSubscription) {
      this.dataOutEventSubscription.unsubscribe();
    }
    if (this.showStopButtonEventSubscription) {
      this.showStopButtonEventSubscription.unsubscribe();
    }
  }
  /**
   * drives to next step
   */
  next(): void {
    let componentValid = true;
    if (this.componentRef) {
      const validatableStepComponent = this.componentRef.instance as NextStepValidatableComponent;
      componentValid =
        typeof validatableStepComponent.validateOnNext === 'function'
          ? validatableStepComponent.validateOnNext()
          : true;
    }
    if (componentValid) {
      this.wizardDriverService.next();
    }
  }
  /**
   * drives to previous step
   */
  prev(): void {
    this.showStopButton = false;
    this.wizardDriverService.prev();
  }
  /**
   * Preserves a copy of state members of a component to be loaded next time component
   * is loaded in screen
   * @see ../utils/member-state.ts decorators
   */
  private saveStepState(): void {
    if (!this.componentRef || !this.currentStepName) {
      return;
    }
    const stateWithMembersComponent = asWithStateMemberType(this.componentRef);
    if (!!stateWithMembersComponent) {
      const stepState = stateWithMembersComponent.StateMembers();
      this.stepStateStore[this.currentStepName] = stepState;
    }
  }
  onNavClose() {
    this.control.close();
  }
  onClose(): void {
    this.control.close();
    if (this.wizardDef.goBackOnclose) {
      this.location.back();
    }
  }
}
